import { Icon } from '@iconify/react';
import { useHasMenu } from '../../../auth/access';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { config } from '../../../config';
import { logError } from '../../../utils/logger';
import type { QuotationPdfFile } from '../../admin/admin.api';
import { useAuth } from '../../auth/auth.context';
import { Card, ModalWrapper, SectionTitle } from '../../shared/components';
import { ConfirmationModal } from '../../shared/components/feedback';
import DialogShell from '../../tasks/components/DialogShell';
import QuotationPdfViewer from '../../work-implementation/reports/QuotationPdfViewer';
import MonthPicker, { lastMonth, YearMonth } from '../components/MonthPicker';
import SalaryBreakdown from '../components/SalaryBreakdown';
import {
  days,
  downloadBankStatement,
  downloadSalaryRegister,
  fetchBankAdvicePdf,
  fetchPayslipsPdf,
  inr,
  localDate,
  monthLabel,
  PayrollLine,
  PayrollLineDetail,
  RunDetail,
  runsApi,
} from '../payroll.api';

const { ADMIN } = config.USER_ROLES;

const fmtDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

/**
 * One month's payroll. "Calculate" runs the whole month in the database in one go; a change to
 * any input afterwards marks it stale, and every report recalculates a stale unpaid month first.
 * Paying, payslips and bank files are for accounts (and admin); HR calculates and checks.
 */
export default function PayrollPage() {
  const { role } = useAuth();
  // Salary Processing's actions (V30): Edit = calculate, Approve = mark paid, Export = documents.
  const canCalculate = useHasMenu('payroll.processing', 'EDIT');
  const canPay = useHasMenu('payroll.processing', 'APPROVE');
  const canExport = useHasMenu('payroll.processing', 'EXPORT');
  const isAdmin = role === ADMIN;

  const [ym, setYm] = useState<YearMonth>(lastMonth());
  const [run, setRun] = useState<RunDetail | null>(null);
  const [notCalculated, setNotCalculated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lineDetail, setLineDetail] = useState<PayrollLineDetail | null>(null);
  const [paying, setPaying] = useState(false);
  const [payDate, setPayDate] = useState(localDate());
  const [payRef, setPayRef] = useState('');
  const [reopening, setReopening] = useState(false);
  const [pdf, setPdf] = useState<QuotationPdfFile | null>(null);
  const pdfUrl = useRef<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setNotCalculated(false);
    runsApi
      .get(ym.year, ym.month)
      .then(setRun)
      .catch((e: any) => {
        setRun(null);
        if (e?.response?.status === 404) setNotCalculated(true);
        else setError(logError({ message: 'Could not load payroll', error: e, code: 'PAYROLL_LOAD_FAILED' }));
      })
      .finally(() => setLoading(false));
  }, [ym]);

  useEffect(load, [load]);

  useEffect(
    () => () => {
      if (pdfUrl.current) URL.revokeObjectURL(pdfUrl.current);
    },
    []
  );

  const act = async (key: string, fn: () => Promise<void>, failure: string) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      toast.error(logError({ message: failure, error: e, code: `PAYROLL_${key.toUpperCase()}_FAILED` }));
    } finally {
      setBusy(null);
    }
  };

  const calculate = () =>
    act(
      'calculate',
      async () => {
        const r = await runsApi.calculate(ym.year, ym.month);
        setRun(r);
        setNotCalculated(false);
        toast.success(`Payroll for ${monthLabel(ym.year, ym.month)} calculated: ${r.employees} employees, net ${inr(r.totalNet)}.`);
      },
      'Calculation failed'
    );

  const showPdf = (file: QuotationPdfFile) => {
    if (pdfUrl.current) URL.revokeObjectURL(pdfUrl.current);
    pdfUrl.current = file.url;
    setPdf(file);
  };

  // A report recalculates a stale month server-side; reload afterwards so the screen matches it.
  const afterReport = () => {
    if (run?.stale) load();
  };

  const payslips = (employeeId?: number) =>
    act(
      employeeId ? `slip${employeeId}` : 'slips',
      async () => {
        showPdf(await fetchPayslipsPdf(ym.year, ym.month, employeeId));
        afterReport();
      },
      'Could not make the payslips'
    );

  const bankAdvice = () =>
    act(
      'advice',
      async () => {
        showPdf(await fetchBankAdvicePdf(ym.year, ym.month));
        afterReport();
      },
      'Could not make the bank advice'
    );

  const bankExcel = () =>
    act(
      'bankxlsx',
      async () => {
        await downloadBankStatement(ym.year, ym.month);
        afterReport();
      },
      'Could not make the bank statement'
    );

  const registerExcel = () =>
    act(
      'regxlsx',
      async () => {
        await downloadSalaryRegister(ym.year, ym.month);
        afterReport();
      },
      'Could not make the salary register'
    );

  const markPaid = () =>
    act(
      'pay',
      async () => {
        const r = await runsApi.pay(ym.year, ym.month, { paymentDate: payDate, reference: payRef.trim() || null });
        setRun(r);
        setPaying(false);
        toast.success(`Salaries for ${monthLabel(ym.year, ym.month)} marked paid.`);
      },
      'Could not mark the salaries paid'
    );

  const reopen = () =>
    act(
      'reopen',
      async () => {
        const r = await runsApi.reopen(ym.year, ym.month);
        setRun(r);
        setReopening(false);
        toast.success('Month reopened. It can be recalculated and paid again.');
      },
      'Could not reopen the month'
    );

  const openLine = (line: PayrollLine) =>
    act(
      `line${line.id}`,
      async () => {
        setLineDetail(await runsApi.line(ym.year, ym.month, line.id));
      },
      'Could not load the breakdown'
    );

  const paid = run?.status === 'PAID';

  const statusChip = !run ? (
    <Chip label='Not calculated' />
  ) : paid ? (
    <Chip color='success' icon={<Icon icon='mdi:check-circle' />} label='Paid' />
  ) : run.stale ? (
    <Chip color='warning' icon={<Icon icon='mdi:alert' />} label='Calculated — out of date' />
  ) : (
    <Chip color='info' label='Calculated' />
  );

  const totals: [string, string][] = run
    ? [
        ['Employees', String(run.employees)],
        ['Gross', inr(run.totalGross)],
        ['Deductions', inr(run.totalDeductions)],
        ['Net pay', inr(run.totalNet)],
        ['Employer contributions', inr(run.totalEmployer)],
      ]
    : [];

  return (
    <Stack gap={3}>
      <SectionTitle title='PAYROLL' action={<MonthPicker value={ym} onChange={setYm} />} />
      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}

      <Card>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' gap={2} alignItems={{ md: 'center' }}>
          <Stack gap={0.75}>
            <Stack direction='row' alignItems='center' gap={1.5}>
              <Typography variant='h5' fontWeight={700}>
                {monthLabel(ym.year, ym.month)}
              </Typography>
              {statusChip}
            </Stack>
            {run && (
              <Typography variant='body2' color='text.secondary'>
                Calculated {fmtDateTime(run.calculatedAt)}
                {run.calculatedByName ? ` by ${run.calculatedByName}` : ''}
                {paid &&
                  ` · Paid on ${run.paymentDate ? new Date(`${run.paymentDate}T00:00:00`).toLocaleDateString('en-IN') : '—'}${
                    run.paymentReference ? ` (ref. ${run.paymentReference})` : ''
                  }${run.paidByName ? `, marked by ${run.paidByName}` : ''}`}
              </Typography>
            )}
            {notCalculated && (
              <Typography variant='body2' color='text.secondary'>
                This month has not been calculated yet. One click works it out for every employee from attendance, leave, holidays,
                salaries and the payroll rules.
              </Typography>
            )}
          </Stack>
          <Stack direction='row' gap={1} flexWrap='wrap' justifyContent='flex-end'>
            {canCalculate && !paid && (
              <Button
                size='large'
                variant='contained'
                color={run?.stale ? 'warning' : 'primary'}
                startIcon={<Icon icon={run ? 'mdi:refresh' : 'mdi:calculator-variant-outline'} />}
                disabled={busy !== null}
                onClick={calculate}
                sx={{ px: 3 }}
              >
                {busy === 'calculate' ? 'Calculating…' : run ? 'Recalculate' : 'Calculate payroll'}
              </Button>
            )}
            {canPay && run && !paid && (
              <Button
                size='large'
                variant='contained'
                color='success'
                startIcon={<Icon icon='mdi:bank-transfer-out' />}
                disabled={busy !== null}
                onClick={() => {
                  setPayDate(localDate());
                  setPayRef('');
                  setPaying(true);
                }}
              >
                Mark salaries paid
              </Button>
            )}
            {isAdmin && paid && (
              <Button variant='outlined' color='warning' startIcon={<Icon icon='mdi:lock-open-variant-outline' />} disabled={busy !== null} onClick={() => setReopening(true)}>
                Reopen
              </Button>
            )}
          </Stack>
        </Stack>

        {run?.stale && !paid && (
          <Alert
            severity='warning'
            action={
              <Button color='inherit' size='small' onClick={calculate} disabled={busy !== null}>
                Recalculate
              </Button>
            }
          >
            Attendance, leave, salary or rules changed after calculation — Recalculate. Reports recalculate it automatically before they are
            made; it cannot be marked paid until it is up to date.
          </Alert>
        )}

        {run && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
            {totals.map(([k, v]) => (
              <Box key={k} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant='caption' color='text.secondary'>
                  {k}
                </Typography>
                <Typography variant='h6' fontWeight={700}>
                  {v}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {run && (
          <Stack direction='row' gap={1} flexWrap='wrap'>
            <Button variant='outlined' startIcon={<Icon icon='mdi:microsoft-excel' />} disabled={busy !== null} onClick={registerExcel}>
              {busy === 'regxlsx' ? 'Preparing…' : 'Salary register Excel'}
            </Button>
            {canExport && (
              <>
                <Button variant='outlined' startIcon={<Icon icon='mdi:file-pdf-box' />} disabled={busy !== null} onClick={() => payslips()}>
                  {busy === 'slips' ? 'Preparing…' : 'Payslips PDF'}
                </Button>
                <Button variant='outlined' startIcon={<Icon icon='mdi:bank-outline' />} disabled={busy !== null} onClick={bankAdvice}>
                  {busy === 'advice' ? 'Preparing…' : 'Bank advice PDF'}
                </Button>
                <Button variant='outlined' startIcon={<Icon icon='mdi:file-excel-outline' />} disabled={busy !== null} onClick={bankExcel}>
                  {busy === 'bankxlsx' ? 'Preparing…' : 'Bank statement Excel'}
                </Button>
              </>
            )}
          </Stack>
        )}
      </Card>

      {run && (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell align='right'>Monthly gross</TableCell>
                <TableCell align='right'>Pay days</TableCell>
                <TableCell align='right'>LOP</TableCell>
                <TableCell align='right'>Payable days</TableCell>
                <TableCell align='right'>Earned gross</TableCell>
                <TableCell align='right'>Deductions</TableCell>
                <TableCell align='right'>Net pay</TableCell>
                <TableCell>Payment</TableCell>
                {canExport && <TableCell align='center'>Slip</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {run.lines.length === 0 && (
                <TableRow>
                  <TableCell {...({ colSpan: canExport ? 10 : 9 } as object)}>
                    <Typography variant='body2' color='text.secondary'>
                      No employees in this month.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {run.lines.map((l) => (
                <TableRow key={l.id} hover onClick={() => openLine(l)} sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Typography variant='body2' fontWeight={600}>
                      {l.name}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {[l.code, l.designation || l.department].filter(Boolean).join(' · ')}
                    </Typography>
                    {l.remarks && (
                      <Typography variant='caption' color='warning.main' display='block'>
                        {l.remarks}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align='right'>{inr(l.monthlyGross)}</TableCell>
                  <TableCell align='right'>{days(l.payDays)}</TableCell>
                  <TableCell align='right' sx={{ color: l.lopDays > 0 ? 'error.main' : undefined }}>
                    {days(l.lopDays)}
                  </TableCell>
                  <TableCell align='right'>{days(l.payableDays)}</TableCell>
                  <TableCell align='right'>{inr(l.earnedGross)}</TableCell>
                  <TableCell align='right'>{inr(l.totalDeductions)}</TableCell>
                  <TableCell align='right' sx={{ fontWeight: 700, color: l.netPay < 0 ? 'error.main' : undefined }}>
                    {inr(l.netPay)}
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>{l.paymentMode || '—'}</Typography>
                    {l.paymentMode === 'BANK' && !l.bankAccountNo && (
                      <Typography variant='caption' color='error'>
                        No bank account
                      </Typography>
                    )}
                  </TableCell>
                  {canExport && (
                    <TableCell align='center'>
                      <Tooltip title='Payslip PDF'>
                        <span>
                          <IconButton
                            size='small'
                            disabled={busy !== null}
                            onClick={(e) => {
                              e.stopPropagation();
                              payslips(l.employeeId);
                            }}
                          >
                            <Icon icon='mdi:file-pdf-box' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ModalWrapper open={lineDetail !== null} onClose={() => setLineDetail(null)} maxWidth='md' sx={{ overflowY: 'auto' }}>
        <SectionTitle
          title='SALARY BREAKDOWN'
          action={
            <Button onClick={() => setLineDetail(null)} startIcon={<Icon icon='mdi:close' />}>
              Close
            </Button>
          }
        />
        {lineDetail && <SalaryBreakdown line={lineDetail} />}
      </ModalWrapper>

      <DialogShell
        open={paying}
        title='MARK SALARIES PAID'
        onClose={() => setPaying(false)}
        onSubmit={markPaid}
        submitLabel='Mark paid'
        submitColor='success'
        busy={busy === 'pay'}
        disabled={!payDate}
      >
        <Typography variant='body2'>
          {run?.employees} employees, net <b>{inr(run?.totalNet)}</b> for {monthLabel(ym.year, ym.month)}. Once paid, the month is locked and
          employees can see the salary in My Salary.
        </Typography>
        <TextField
          label='Payment date'
          type='date'
          size='small'
          value={payDate}
          onChange={(e) => setPayDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label='Reference (UTR / cheque no. / batch)'
          size='small'
          value={payRef}
          onChange={(e) => setPayRef(e.target.value)}
        />
      </DialogShell>

      <ConfirmationModal
        open={reopening}
        title='Reopen this month?'
        message={`${monthLabel(ym.year, ym.month)} will no longer be marked paid, so it can be recalculated. Employees stop seeing it in My Salary until it is paid again.`}
        confirmText='Reopen'
        onConfirm={reopen}
        onClose={() => setReopening(false)}
        loading={busy === 'reopen'}
      />

      <QuotationPdfViewer
        file={pdf}
        title={pdf?.filename ?? 'Salary PDF'}
        onClose={() => {
          if (pdfUrl.current) URL.revokeObjectURL(pdfUrl.current);
          pdfUrl.current = null;
          setPdf(null);
        }}
      />
    </Stack>
  );
}
